import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import "./modals/styles/modalStyle.css";

/**
 * Check if Web Speech API is available in this browser.
 */
const isSpeechSupported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

/**
 * Helper: extract unique previous values from serviceData for a given field name.
 * Mirrors the getPreviousValues logic in addToMap.js.
 */
function getPreviousValues(serviceData, fieldName) {
    if (!serviceData || !Array.isArray(serviceData)) return [];
    return [
        ...new Set(
            serviceData
                .map((row) => row[fieldName])
                .filter((x) => x !== undefined && x !== null && String(x).trim() !== "")
        ),
    ];
}

/**
 * Build a vocabulary map from inputObjectArray + serviceData.
 * Returns an array of { phrase, fieldName, value } sorted longest-first.
 *
 * - dropdown fields: split field.values by ", "
 * - textDropdown fields: extract unique values from serviceData
 */
function buildVocabulary(inputObjectArray, serviceData) {
    if (!inputObjectArray) return [];
    const entries = [];

    for (const field of inputObjectArray) {
        if (!field.display) continue;

        if (field.type === "dropdown" && field.values) {
            const options = field.values.split(", ").filter(Boolean);
            for (const opt of options) {
                entries.push({
                    phrase: opt.toLowerCase(),
                    fieldName: field.name,
                    value: opt,
                });
            }
        } else if (field.type === "textDropdown") {
            const prevValues = getPreviousValues(serviceData, field.name);
            for (const val of prevValues) {
                const strVal = String(val);
                entries.push({
                    phrase: strVal.toLowerCase(),
                    fieldName: field.name,
                    value: strVal,
                });
            }
        }
    }

    // Sort longest phrases first so "Dry Barrel" matches before "Dry"
    entries.sort((a, b) => b.phrase.length - a.phrase.length);
    return entries;
}

/**
 * Match a transcript against the vocabulary.
 * Returns { fieldName: value } for each matched field.
 * Consumes matched portions so they don't match again.
 */
function matchTranscript(transcript, vocabulary) {
    if (!transcript || !vocabulary.length) return {};
    let remaining = transcript.toLowerCase();
    const matched = {};

    for (const entry of vocabulary) {
        // Skip if we already matched a value for this field
        if (matched[entry.fieldName]) continue;

        if (remaining.includes(entry.phrase)) {
            matched[entry.fieldName] = entry.value;
            // Remove the matched phrase so it doesn't interfere with other matches
            remaining = remaining.replace(entry.phrase, " ").trim();
        }
    }

    return matched;
}

/**
 * VoiceInput — Mic button with Web Speech API transcription and vocabulary matching.
 *
 * Tier 1: Client-side keyword matching (FREE).
 * Tier 2: Optional "Smart Mode" that sends transcript to POST /api/voiceCommand
 *         for Gemini-powered field extraction.
 *
 * @param {Object} props
 * @param {Array} props.inputObjectArray - field definitions for the current asset type
 * @param {Array} props.previousValues - serviceData array for textDropdown lookups
 * @param {Function} props.onFieldsMatched - callback({ fieldName: value, ... })
 * @param {string} props.assetType - current asset type (e.g., "Hydrant")
 */
function VoiceInput({ inputObjectArray, previousValues, onFieldsMatched, assetType, children }) {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [matchedFields, setMatchedFields] = useState({});
    const [smartMode, setSmartMode] = useState(false);
    const [smartLoading, setSmartLoading] = useState(false);
    const [error, setError] = useState(null);
    const recognitionRef = useRef(null);
    const accumulatedTranscriptRef = useRef("");
    const userRequestedStopRef = useRef(false);

    // Build vocabulary from field definitions and existing data
    const vocabulary = useMemo(
        () => buildVocabulary(inputObjectArray, previousValues),
        [inputObjectArray, previousValues]
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                try { recognitionRef.current.abort(); } catch { /* ignore */ }
            }
        };
    }, []);

    /**
     * Tier 2: Send transcript to the server for Gemini-powered parsing.
     */
    const sendToSmartMode = useCallback(async (finalTranscript) => {
        if (!finalTranscript.trim()) return;

        setSmartLoading(true);
        setError(null);

        try {
            // Strip field definitions to minimal payload to save tokens
            const fieldDefs = (inputObjectArray || [])
                .filter((f) => f.display)
                .map((f) => ({
                    name: f.name,
                    displayName: f.displayName,
                    type: f.type,
                    values: f.values || null,
                }));

            const { data } = await axios.post("/api/voiceCommand", {
                transcript: finalTranscript,
                assetType,
                fieldDefinitions: fieldDefs,
            });

            if (data.fields && Object.keys(data.fields).length > 0) {
                setMatchedFields(data.fields);
                // Auto-apply after a short delay so user can see what was matched
                setTimeout(() => {
                    onFieldsMatched(data.fields);
                    setMatchedFields({});
                    setTranscript("");
                }, 1500);
            } else {
                setError("No fields matched from your voice input.");
            }
        } catch (err) {
            console.warn("[VoiceInput] Smart Mode failed, falling back to local matching:", err);
            // Fall back to local keyword matching
            const localMatched = matchTranscript(finalTranscript, vocabulary);
            if (Object.keys(localMatched).length > 0) {
                setMatchedFields(localMatched);
                setTimeout(() => {
                    onFieldsMatched(localMatched);
                    setMatchedFields({});
                    setTranscript("");
                }, 1500);
            } else {
                setError("Smart Mode unavailable. No local matches found either.");
            }
        } finally {
            setSmartLoading(false);
        }
    }, [inputObjectArray, assetType, vocabulary, onFieldsMatched]);

    /**
     * Process the final transcript — either keyword match or smart mode.
     */
    const processTranscript = useCallback((finalTranscript) => {
        if (!finalTranscript.trim()) return;

        if (smartMode) {
            sendToSmartMode(finalTranscript);
        } else {
            // Tier 1: local keyword matching
            const matched = matchTranscript(finalTranscript, vocabulary);
            if (Object.keys(matched).length > 0) {
                setMatchedFields(matched);
                // Auto-apply after 1.5s so user can see matched chips
                setTimeout(() => {
                    onFieldsMatched(matched);
                    setMatchedFields({});
                    setTranscript("");
                }, 1500);
            } else {
                setError("No matching field values found. Try saying specific values like \"Red\" or \"Dry Barrel\".");
                setTimeout(() => setError(null), 4000);
            }
        }
    }, [smartMode, vocabulary, onFieldsMatched, sendToSmartMode]);

    /**
     * Start / stop listening.
     */
    const toggleListening = useCallback(() => {
        if (isListening) {
            // User tapped to stop — mark so onend will process accumulated transcript
            userRequestedStopRef.current = true;
            if (recognitionRef.current) {
                try { recognitionRef.current.stop(); } catch { /* ignore */ }
            }
            setIsListening(false);
            return;
        }

        // Start
        setError(null);
        setMatchedFields({});
        setTranscript("");
        accumulatedTranscriptRef.current = "";
        userRequestedStopRef.current = false;

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setError("Voice input is not supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true; // Keep listening until user taps stop (fixes "stops immediately")
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            let interimTranscript = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    const text = result[0].transcript;
                    const sep = accumulatedTranscriptRef.current ? " " : "";
                    accumulatedTranscriptRef.current += sep + text;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            // Show accumulated final + current interim
            const display =
                accumulatedTranscriptRef.current +
                (interimTranscript ? " " + interimTranscript : "");
            setTranscript(display.trim());
        };

        recognition.onerror = (event) => {
            console.warn("[VoiceInput] Speech recognition error:", event.error);
            if (event.error === "no-speech") {
                setError("No speech detected. Please try again.");
            } else if (event.error === "not-allowed") {
                setError("Microphone access denied. Please allow microphone permissions.");
            } else {
                setError(`Voice error: ${event.error}`);
            }
            setIsListening(false);
            userRequestedStopRef.current = false;
            setTimeout(() => setError(null), 4000);
        };

        recognition.onend = () => {
            setIsListening(false);
            // Process accumulated transcript only when user explicitly stopped (tap mic again)
            if (userRequestedStopRef.current && accumulatedTranscriptRef.current.trim()) {
                processTranscript(accumulatedTranscriptRef.current.trim());
            }
            userRequestedStopRef.current = false;
        };

        recognitionRef.current = recognition;
        recognition.start();
    }, [isListening, processTranscript]);

    // Don't render if Web Speech API is not supported
    if (!isSpeechSupported) return null;

    // Get display-friendly field names for matched chips
    const fieldDisplayNames = {};
    if (inputObjectArray) {
        for (const f of inputObjectArray) {
            fieldDisplayNames[f.name] = f.displayName || f.name;
        }
    }

    const hasFeedback = transcript || Object.keys(matchedFields).length > 0 || error;

    return (
        <div className="aiToolbar">
            {/* ── Controls row: Mic · AI Assist · Smart toggle ── */}
            <div className="aiToolbar-controls">
                <Tooltip title={isListening ? "Stop listening" : "Voice input — speak field values"}>
                    <IconButton
                        onClick={toggleListening}
                        disabled={smartLoading}
                        size="small"
                        sx={{
                            color: isListening ? "#fff" : "#3f51b5",
                            backgroundColor: isListening ? "#d32f2f" : "transparent",
                            border: isListening ? "none" : "1px solid #3f51b5",
                            width: 32,
                            height: 32,
                            animation: isListening ? "pulse 1.5s infinite" : "none",
                            "@keyframes pulse": {
                                "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                            },
                            "&:hover": {
                                backgroundColor: isListening ? "#b71c1c" : "rgba(63,81,181,0.08)",
                            },
                        }}
                    >
                        {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>

                {/* Extra actions passed from parent (e.g. AI Assist button) */}
                {children}

                {smartLoading && <CircularProgress size={16} />}

                {/* Smart Mode toggle — pushed to the right */}
                <Tooltip title="Smart Mode uses AI for complex voice commands">
                    <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                        <AutoFixHighIcon
                            sx={{ fontSize: 14, color: smartMode ? "#3f51b5" : "#bbb" }}
                        />
                        <Switch
                            size="small"
                            checked={smartMode}
                            onChange={(e) => setSmartMode(e.target.checked)}
                            sx={{ transform: "scale(0.75)", mx: -0.25 }}
                        />
                        <span
                            style={{
                                fontSize: "10px",
                                color: smartMode ? "#3f51b5" : "#999",
                                fontWeight: smartMode ? 600 : 400,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Smart
                        </span>
                    </div>
                </Tooltip>
            </div>

            {/* ── Feedback area (transcript / chips / error) ── */}
            {hasFeedback && (
                <div className="aiToolbar-feedback">
                    {transcript && (
                        <div className="aiToolbar-transcript">
                            &ldquo;{transcript}&rdquo;
                        </div>
                    )}

                    {Object.keys(matchedFields).length > 0 && (
                        <div className="aiToolbar-chips">
                            {Object.entries(matchedFields).map(([fieldName, value]) => (
                                <Chip
                                    key={fieldName}
                                    label={`${fieldDisplayNames[fieldName] || fieldName}: ${value}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: "11px", height: 22 }}
                                />
                            ))}
                        </div>
                    )}

                    {error && (
                        <div className="aiToolbar-error">{error}</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default VoiceInput;
